// Pure tree helpers for the administrativeAreas page — the backend returns a FLAT node
// list (each with parent_id); these assemble it and answer the shape questions the
// page / peek / drawers ask. No i18n, no React — the components compose labels.

import type { AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';
import type { OptionRow } from '../../../hooks/useOptions.js';

export interface GeoTreeNode extends AdministrativeAreaNode {
  children: GeoTreeNode[];
}

// Assemble the flat list into nested trees (roots first). The backend already
// orders by display_order then name, so children land in curated order.
export function assembleTree(flat: AdministrativeAreaNode[]): GeoTreeNode[] {
  const byId = new Map<number, GeoTreeNode>();
  for (const n of flat) byId.set(n.id, { ...n, children: [] });
  const roots: GeoTreeNode[] = [];
  for (const n of flat) {
    const node = byId.get(n.id)!;
    if (n.parent_id === null) roots.push(node);
    else byId.get(n.parent_id)?.children.push(node);
  }
  return roots;
}

// A top-level COUNTRY grouping row for the list tree — the administrativeArea roots nest under
// it, so the tree reads "country ▸ region ▸ …" and countries never interleave. Virtual
// (no administrativeArea id); its table id is `country:<iso>`.
export interface GeoCountryRow {
  kind: 'country';
  iso: string;
  name: string; // resolved country display name (label injected — this module stays i18n-free)
  children: GeoTreeNode[]; // the country's administrativeArea roots (assembled subtrees)
}
export type GeoListRow = GeoCountryRow | GeoTreeNode;
export function isCountryRow(row: GeoListRow): row is GeoCountryRow {
  return (row as GeoCountryRow).kind === 'country';
}

// Assemble the flat list into COUNTRY-grouped rows: one row per distinct country (sorted
// by display name), each wrapping that country's administrativeArea roots in curated order.
// `countryLabel` resolves an ISO to its display name (injected — no i18n here).
export function assembleCountryTree(flat: AdministrativeAreaNode[], countryLabel: (iso: string) => string): GeoListRow[] {
  const roots = assembleTree(flat);
  const byCountry = new Map<string, GeoTreeNode[]>();
  for (const root of roots) {
    const arr = byCountry.get(root.country_iso_code) ?? [];
    arr.push(root);
    byCountry.set(root.country_iso_code, arr);
  }
  return [...byCountry.keys()]
    .map((iso) => ({ kind: 'country' as const, iso, name: countryLabel(iso), children: byCountry.get(iso)! }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Names from root → node (inclusive). The caller prefixes the country.
export function breadcrumbNames(flat: AdministrativeAreaNode[], id: number): string[] {
  const byId = new Map(flat.map((n) => [n.id, n] as const));
  const out: string[] = [];
  let cur: number | null = id;
  while (cur !== null) {
    const n: AdministrativeAreaNode | undefined = byId.get(cur);
    if (!n) break;
    out.unshift(n.name);
    cur = n.parent_id;
  }
  return out;
}

// A node + all its descendants — the set the Move picker excludes (no cycles).
export function subtreeIds(flat: AdministrativeAreaNode[], id: number): Set<number> {
  const childrenOf = new Map<number, number[]>();
  for (const n of flat) {
    if (n.parent_id !== null) {
      const a = childrenOf.get(n.parent_id) ?? [];
      a.push(n.id);
      childrenOf.set(n.parent_id, a);
    }
  }
  const out = new Set<number>([id]);
  const stack = [id];
  while (stack.length > 0) {
    const pid = stack.pop()!;
    for (const cid of childrenOf.get(pid) ?? []) {
      out.add(cid);
      stack.push(cid);
    }
  }
  return out;
}

// Direct children count (the peek's "Areas inside") — derived from the flat list.
export function childCount(flat: AdministrativeAreaNode[], id: number): number {
  return flat.reduce((acc, n) => (n.parent_id === id ? acc + 1 : acc), 0);
}

// The siblings of a node (same parent scope) in display order — the Reorder set.
export function siblingsOf(flat: AdministrativeAreaNode[], id: number): AdministrativeAreaNode[] {
  const node = flat.find((n) => n.id === id);
  if (!node) return [];
  return flat.filter((n) => n.parent_id === node.parent_id);
}

// Distinct whitelisted-country codes present in the tree (the parent field's country root and the list's country rows).
export function distinctCountries(flat: AdministrativeAreaNode[]): string[] {
  return [...new Set(flat.map((n) => n.country_iso_code))];
}

// Direct children of a node (or the roots when id is null), in the backend's curated
// display_order,name order (the flat list already arrives sorted). The peek's "areas
// inside" list + the reorder-children set.
export function childrenOf(flat: AdministrativeAreaNode[], id: number | null): AdministrativeAreaNode[] {
  return flat.filter((n) => n.parent_id === id);
}

// The ancestor trail root→node (inclusive) as {id,name} pairs — the peek breadcrumb,
// where each ancestor links to its own peek and the last is the current node.
// (breadcrumbNames gives only the names; this keeps ids for navigation.)
export function breadcrumbTrail(flat: AdministrativeAreaNode[], id: number): { id: number; name: string }[] {
  const byId = new Map(flat.map((n) => [n.id, n] as const));
  const out: { id: number; name: string }[] = [];
  let cur: number | null = id;
  while (cur !== null) {
    const n: AdministrativeAreaNode | undefined = byId.get(cur);
    if (!n) break;
    out.unshift({ id: n.id, name: n.name });
    cur = n.parent_id;
  }
  return out;
}

/** The parent field's value for a country's top level (a new or moved area with no parent area). */
export const COUNTRY_VALUE = 'country:';

/** The parent field's rows for Add and Move, on the NAVIGATOR (ADR-080 §14): the COUNTRIES at the root — a country
 *  row is that country's top level (`country:<ISO>`) — each country's top areas inside it, every area inside its
 *  parent; `path` = the country and the areas above, top first (under a search match and under the chosen). A
 *  disabled area is an inactive row (a field never offers it). `excludeSubtreeOf` leaves out an area and everything
 *  inside it (Move — no cycle offerable). PURE — the country's name is injected. */
export function parentRows(
  flat: AdministrativeAreaNode[],
  opts: { countries: string[]; countryLabel: (iso: string) => string; excludeSubtreeOf?: number },
): OptionRow[] {
  const excluded = opts.excludeSubtreeOf != null ? subtreeIds(flat, opts.excludeSubtreeOf) : new Set<number>();
  const byId = new Map(flat.map((n) => [n.id, n] as const));
  const named = opts.countries.map((iso) => ({ iso, name: opts.countryLabel(iso) })).sort((a, b) => a.name.localeCompare(b.name));
  const countryOf = new Map(named.map((c) => [c.iso, c.name] as const));
  const pathOf = (n: AdministrativeAreaNode): string => {
    const names: string[] = [];
    let cur = n.parent_id;
    while (cur !== null) {
      const p = byId.get(cur);
      if (!p) break;
      names.unshift(p.name);
      cur = p.parent_id;
    }
    return [countryOf.get(n.country_iso_code) ?? n.country_iso_code, ...names].join(' / ');
  };
  const countries: OptionRow[] = named.map((c) => ({ id: `${COUNTRY_VALUE}${c.iso}`, label: c.name, pickable: true, group: null, inside: null }));
  const areas: OptionRow[] = flat
    .filter((n) => countryOf.has(n.country_iso_code) && !excluded.has(n.id))
    .map((n) => ({
      id: String(n.id),
      label: n.name,
      pickable: true,
      inactive: n.is_disabled,
      group: null,
      inside: n.parent_id === null ? `${COUNTRY_VALUE}${n.country_iso_code}` : String(n.parent_id),
      path: pathOf(n),
    }));
  return [...countries, ...areas];
}
